import { useEffect, useMemo, useState } from 'react';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { Avatar, YesNo, fullName, joinParts, str } from '~/components/module/setup/cells';
import { contactAdapter } from '~/apis/contact';
import fileApi, { IRingtone } from '~/apis/file';
import { DEFAULT_DIAL_CODE } from '~/utils/constants/countries';
import { TEXT_LANGUAGE_OPTIONS, VOICE_LANGUAGE_OPTIONS } from '~/utils/constants/profileOptions';
import { IFormField } from '~/utils/interface/setup';

// "Homecoming.ogg" -> "Homecoming", "Some_Tone.ogg" -> "Some Tone" (same as the app)
const ringtoneLabel = (name: string) => (name.split('.').slice(0, -1).join('.') || name).replace(/_/g, ' ');

const DEFAULT_RINGTONE = 'Homecoming.ogg';

const Contacts = () => {
  const [ringtones, setRingtones] = useState<IRingtone[]>([]);

  useEffect(() => {
    fileApi
      .getRingtones()
      .then((resp) => setRingtones(resp.data.data))
      .catch(() => setRingtones([]));
  }, []);

  const fields = useMemo<IFormField[]>(
    () => [
      { name: 'avatarImage', labelKey: 'pages.setup.fields.avatar', type: 'image' },
      { name: 'username', labelKey: 'pages.setup.fields.username', type: 'text', required: true },
      { name: 'email', labelKey: 'pages.setup.fields.email', type: 'email', required: true },
      { name: 'firstName', labelKey: 'pages.setup.fields.firstName', type: 'text', required: true },
      { name: 'lastName', labelKey: 'pages.setup.fields.lastName', type: 'text', required: true },
      { name: 'phoneNumber', labelKey: 'pages.setup.fields.phoneNumber', type: 'phone', countryName: 'countryCode', required: true },
      {
        name: 'languageCode',
        labelKey: 'pages.setup.profile.languageText',
        type: 'select',
        required: true,
        options: TEXT_LANGUAGE_OPTIONS,
      },
      {
        name: 'languageVoiceCode',
        labelKey: 'pages.setup.profile.languageVoice',
        type: 'select',
        required: true,
        options: VOICE_LANGUAGE_OPTIONS,
      },
      {
        name: 'ringtone',
        labelKey: 'pages.setup.contacts.ringtone',
        type: 'select',
        emptyLabelKey: 'pages.setup.contacts.noRingtone',
        options: ringtones.map((ringtone) => ({ value: ringtone.name, label: ringtoneLabel(ringtone.name) })),
      },
      { name: 'isEmergency', labelKey: 'pages.setup.contacts.isEmergency', type: 'checkbox' },
      { name: 'isFallDetection', labelKey: 'pages.setup.contacts.isFallDetection', type: 'checkbox' },
      // like the app, the alert options belong to the Alerts list: always listed, editable once it is ticked
      {
        name: 'alertOptions',
        labelKey: 'pages.setup.contacts.alertOptions',
        type: 'heading',
        indent: true,
        disabledWhen: (values) => values.isFallDetection !== true,
      },
      { name: 'alertText', labelKey: 'pages.setup.contacts.alertText', type: 'checkbox', indent: true, disabledWhen: (values) => values.isFallDetection !== true },
      { name: 'alertSound', labelKey: 'pages.setup.contacts.alertSound', type: 'checkbox', indent: true, disabledWhen: (values) => values.isFallDetection !== true },
      { name: 'alertVibration', labelKey: 'pages.setup.contacts.alertVibration', type: 'checkbox', indent: true, disabledWhen: (values) => values.isFallDetection !== true },
    ],
    [ringtones]
  );

  return (
    <CollectionPage
      i18nPrefix='pages.setup.contacts'
      module='contact'
      adapter={contactAdapter}
      fields={fields}
      defaultForm={{
        countryCode: DEFAULT_DIAL_CODE,
        languageCode: 'en-US',
        languageVoiceCode: 'en-US',
        ringtone: DEFAULT_RINGTONE,
        isEmergency: false,
        isFallDetection: false,
        alertText: true,
        alertSound: true,
        alertVibration: false,
      }}
      getTitle={(form) => fullName(form) || str(form.username)}
      columns={[
        {
          key: 'name',
          labelKey: 'pages.setup.table.name',
          render: (form) => (
            <Avatar src={form.avatarImage} name={fullName(form) || str(form.username)} />
          ),
        },
        { key: 'username', labelKey: 'pages.setup.fields.username', render: (form) => str(form.username) },
        { key: 'email', labelKey: 'pages.setup.fields.email', render: (form) => str(form.email) },
        {
          key: 'phone',
          labelKey: 'pages.setup.fields.phoneNumber',
          render: (form) => joinParts([form.countryCode, form.phoneNumber], ' '),
        },
        { key: 'sos', labelKey: 'pages.setup.contacts.isEmergency', render: (form) => <YesNo value={form.isEmergency} /> },
        { key: 'alerts', labelKey: 'pages.setup.contacts.isFallDetection', render: (form) => <YesNo value={form.isFallDetection} /> },
      ]}
    />
  );
};

export default Contacts;
